import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useState } from 'react'
import { EmcNetworkScanButton } from '../../../components/Dialogs/components/NetworkScanButton'
import { Emc, MyPvIo } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getIsEmcOnline } from 'src/Libs/flow/installation/utils'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useFetchScanEmcMyPv } from '../../hooks'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useDialogActions } from '../../../components/Dialogs/hooks'

//#region Main Dialog

/** Allow only numeric characters. Also allows an empty string. */
const MY_PV_PAIRING_ID_REGEX = /^\d*$/i

export interface MyPvPairingDialogProps {
  installationId: number | null
  emc: Emc | null
  myPv: MyPvIo
  submitPairingId: (id: string) => void
  onClose?: () => void
}

// Todo: test with real device
export const MyPvPairingDialog: React.FC<MyPvPairingDialogProps> = ({ installationId, emc, myPv, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const [pairingId, setPairingId] = useState(myPv.pairingId)

  const isSame = myPv.pairingId === pairingId

  const handleSubmit = (): void => {
    submitPairingId(pairingId)

    handleOnClose()
  }

  const handleOnClose = (): void => {
    onClose?.()
  }

  const isEmcOnline = getIsEmcOnline(emc)

  const [startNetworkScanner, setStartNetworkScanner] = useState(false)

  const primaryName = myPv.isNew ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')
  const dialogActions = useDialogActions({
    primary: {
      name: primaryName,
      onClick: handleSubmit,
      disabled: isSame,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth={'md'} onClose={handleOnClose} title={t('installation.productEditor.pairingEditor.title.mypvheater')} actions={dialogActions}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, my: 2 }}>
        <TextField
          type="number"
          name="MyPV pairing id"
          label={t('productEditor.pairingEditor.pairingID.mypv')}
          value={pairingId}
          onChange={value => {
            if (MY_PV_PAIRING_ID_REGEX.test(value)) setPairingId(value)
          }}
          maxLength={200}
        />
        <EmcNetworkScanButton
          emc={emc}
          installationId={installationId}
          onClick={() => setStartNetworkScanner(!startNetworkScanner)}
          buttonLabel={t('installation.productEditor.scanLocalNetwork')}
          disabled={!isEmcOnline}
        />
      </Box>
      {startNetworkScanner && (
        <MyPvPairingEditor
          installationId={installationId}
          emcId={emc?.deviceId.toString()}
          setPairingId={setPairingId}
          currentPairingId={pairingId}
        />
      )}
    </Dialog>
  )
}

//#endregion

//#region Dialog Editor
interface MyPvPairingEditorProps {
  installationId: number | null
  emcId: string | undefined
  setPairingId: (pairingId: string) => void
  currentPairingId: string | undefined
}

const MyPvPairingEditor: React.FC<MyPvPairingEditorProps> = ({ installationId, emcId, setPairingId, currentPairingId }) => {
  const { t } = useCustomTranslation()

  const { data, isPending, error } = useFetchScanEmcMyPv(installationId, emcId)

  if (error) {
    return <Typography color="error">{error.message}</Typography>
  }

  if (isPending || !data) {
    return (
      <Box textAlign={'center'}>
        <CircularProgress />
      </Box>
    )
  }

  if (!data.serial) {
    return <Alert severity="error" text={t('installation.productEditor.scanNetwork.noResults')} />
  }

  const isSame = data.serial === currentPairingId

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        py: 2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ minWidth: 100, lineHeight: 1.5 }}>{t('installation.productEditor.scanNetwork.serialNumber')}</Typography>
          <Typography sx={{ fontWeight: 'bold', lineHeight: 1.5 }}>{data.serial}</Typography>
        </Box>
      </Box>
      <Button color={'secondary'} variant={'outlined'} size="large" onClick={() => setPairingId(data.serial)} disabled={isSame}>
        {isSame ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}
//#endregion
